import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useGetCourse } from '../../hooks/useGetCourse.js';
import { useGetPlan } from '../../hooks/useGetPlan.js';
import { useFetch } from '../../hooks/useFetch';
import { useAssociateDiscount } from '../../hooks/useGetDiscount';
import { useState, useEffect, useRef } from 'react';
import * as CONSTANTS from '../../assets/constants';

export const CartModal = () => {
  const { cart, removeFromCart, open, setOpen, clearCart } = useCart();
  const [, handleFetch] = useFetch();
  const [couponCode, setCouponCode] = useState('');
  const [couponActivate, setCouponActivate] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [discountType, setDiscountType] = useState(-1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  // Ref para detectar la transición carrito vacío -> con productos
  const prevCartLengthRef = useRef(cart.length);
  useEffect(() => {
    // Si anteriormente estaba vacío y ahora tiene items, forzar que el cupón se vuelva a añadir/validar
    if (prevCartLengthRef.current === 0 && cart.length > 0) {
      setCouponCode('');
      setCouponActivate(false);
      setDiscount(null);
      setDiscountType(-1);
    }
    prevCartLengthRef.current = cart.length;
  }, [cart.length]);

  // Función para validar si el cupón aplica al carrito
  const isCouponApplicable = (discountObj, cartItems) => {
    if (!discountObj) return false;
    const planArrRaw = discountObj.plan ?? [];
    const courseArrRaw = discountObj.course ?? [];
    const planArr = Array.isArray(planArrRaw) ? planArrRaw : [];
    const courseArr = Array.isArray(courseArrRaw) ? courseArrRaw : [];

    const planInCart = cartItems?.find(
      (item) =>
        item?.type === 'plan' && planArr.includes(item?.courseId || item?.id || item?.planId)
    );
    // Verificar si hay cursos en el carrito que apliquen
    const courseInCart = cartItems.find(
      (item) =>
        item.type !== 'plan' &&
        courseArr.includes(item.courseId || item['course.courseId'] || item.id)
    );
    return !!(planInCart || courseInCart);
  };

  const subtotal = cart.reduce((sum, c) => sum + (c.price || 0), 0);

  // Función para calcular el precio final con descuento
  const calcFinalPrice = (price, type, value) => {
    let finalPrice = price;
    if (type === 0) finalPrice = price - value;
    else if (type === 1) {
      const porcDiscount = (price * value) / 100;
      finalPrice = price - porcDiscount;
    } else {
      console.error('Tipo de cálculo no válido. Debe ser 0 o 1.');
    }
    if (finalPrice < 0) finalPrice = price;
    return finalPrice;
  };

  // Función para activar cupón
  const activateCoupon = (e) => {
    e.preventDefault();
    if (!couponCode.trim()) return;

    const response = handleFetch({
      url: `payments/discounts?&code=${couponCode}&active=true`,
      method: 'GET',
    });
    response.then((r) => {
      if (r?.code) {
        setCouponActivate(false);
        setDiscount(r);
        setDiscountType(r.type);
      } else {
        setDiscount(null);
        setDiscountType(-1);
        setCouponActivate(false);
      }
    });
  };

  // Solo aplicar descuento si el cupón es válido para el carrito
  const couponApplies = discount && !couponActivate && isCouponApplicable(discount, cart);
  const total = couponApplies ? calcFinalPrice(subtotal, discountType, discount.price) : subtotal;

  // Función para limpiar el carrito y el cupón
  const handleClearCart = () => {
    clearCart();
    setCouponCode('');
    setCouponActivate(false);
    setDiscount(null);
    setDiscountType(-1);
  };

  const courseActionButtonPrefix = 'ab_course_';
  const { isLogged, getSessionUser } = useAuthToken();
  const { getCourse } = useGetCourse();
  const { associateDiscount } = useAssociateDiscount();
  const { getPlan } = useGetPlan();
  const navigate = useNavigate();
  const logged = isLogged();
  // Refactor: función para manejar la compra, recibe método de pago
  const handleBuy = (paymentMethod) => {
    const user = getSessionUser();
    const planItem = cart.find((item) => item.type === 'plan');
    const courseItems = cart.filter((item) => item.type !== 'plan');
    const applyDiscountToItem = (item) => {
      const price = item.price || 0;
      if (!discount || couponActivate) return price;

      const planArrRaw = discount.plan ?? [];
      const courseArrRaw = discount.course ?? [];
      const planArr = Array.isArray(planArrRaw) ? planArrRaw : [];
      const courseArr = Array.isArray(courseArrRaw) ? courseArrRaw : [];

      const itemId = item['courseId'] || item['course.courseId'] || item.id || item.planId;
      const appliesToPlan = item.type === 'plan' && planArr.includes(itemId);
      const appliesToCourse = item.type !== 'plan' && courseArr.includes(itemId);
      const applies = appliesToPlan || appliesToCourse;

      if (!applies) return price;
      return calcFinalPrice(price, discountType, discount.price);
    };

    if (logged) {
      const updatedPlan = planItem
        ? {
            id: planItem.id,
            name: planItem.name,
            price: applyDiscountToItem(planItem),
            duration: planItem.duration,
            durationPeriod: planItem.durationPeriod,
            type: planItem.type,
          }
        : undefined;

      const updatedCourse = courseItems.map((item) => ({
        courseId: item['courseId'] || item['course.courseId'],
        name: item['name'] || item['course.title'],
        price: applyDiscountToItem(item),
      }));

      if (discount && discount.code) {
        associateDiscount(user, updatedCourse, updatedPlan, discount, total)
          .then(() => {})
          .finally(() => {
            // no hacemos setProcessing(false) aquí porque seguirá el flujo de compra
          });
      }
      if (planItem) {
        getPlan(user, updatedPlan, false, paymentMethod);
      } else {
        getCourse(user, updatedCourse, false, paymentMethod);
      }
      // Redirigir a página de confirmación o inicio después de la compra
      navigate('/');
    } else {
      // Si no está logueado, redirigir a registro
      navigate('/registro', {
        state: {
          plan: cart.find((item) => item.type === 'plan')
            ? {
                id: cart.find((item) => item.type === 'plan').id,
                name: cart.find((item) => item.type === 'plan').name,
                price: total === 0 ? total : cart.find((item) => item.type === 'plan').price,
                duration: cart.find((item) => item.type === 'plan').duration,
                durationPeriod: cart.find((item) => item.type === 'plan').durationPeriod,
                type: cart.find((item) => item.type === 'plan').type,
              }
            : undefined,
          course: cart
            .filter((item) => item.type !== 'plan')
            .map((item) => ({
              courseId: item['courseId'] || item['course.courseId'],
              name: item['name'] || item['course.title'],
              price: total === 0 ? 0 : item.price,
            })),
          pasarela: paymentMethod,
          couponDiscount: discount ? discount : null,
        },
      });
    }
    setShowPaymentModal(false);
    setOpen(false);
  };
  // Usar la función de compra con el método seleccionado
  const handleContinuePayment = () => {
    if (!selectedPaymentMethod) return;
    handleBuy(selectedPaymentMethod);
  };
  // Usar la función de compra con el método seleccionado
  const handleWithoutPayment = () => {
    //setSelectedPaymentMethod('mercadopago');
    //if (!selectedPaymentMethod) return;
    handleBuy(selectedPaymentMethod);
  };
  return (
    <div
      className={`modal fade show`}
      tabIndex="-1"
      style={{
        display: open ? 'block' : 'none',
        background: 'rgba(0,0,0,0.4)',
        position: 'fixed',
        top: 0,
        right: 0,
        left: 0,
        bottom: 0,
        zIndex: 2000,
      }}
      aria-modal="true"
      role="dialog"
      onClick={() => setOpen(false)}
    >
      <div
        className="modal-dialog modal-dialog-end"
        style={{
          width: 400,
          maxWidth: 400,
          minWidth: 320,
          height: '100vh',
          minHeight: '100vh',
          margin: 0,
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content h-100 d-flex flex-column" style={{ height: '100vh' }}>
          <div className="modal-header text-black">
            <h5 className="modal-title">Carrito de compras</h5>
            <button
              type="button"
              className="btn-close btn-close-black"
              aria-label="Close"
              onClick={() => setOpen(false)}
            ></button>
          </div>
          <div className="modal-body flex-grow-1 overflow-auto">
            {cart.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="fa fa-shopping-cart fa-3x mb-3"></i>
                <p>Tu carrito está vacío.</p>
              </div>
            ) : (
              <ul className="list-group list-group-flush">
                {cart.map((item) => (
                  <li
                    key={item.courseId || item.name}
                    className="list-group-item d-flex align-items-center"
                  >
                    {item.type === 'plan' ? (
                      <div className="flex-grow-1">
                        <div className="fw-bold" style={{ fontSize: 15 }}>
                          {item.name} <span className="badge bg-info ms-2">Plan</span>
                        </div>
                        <div className="text-success fw-bold" style={{ fontSize: 14 }}>
                          COP ${item.price?.toLocaleString('es-CO')}
                        </div>
                      </div>
                    ) : (
                      <>
                        <img
                          src={item['course.imageUrl']}
                          alt={item['course.title']}
                          style={{
                            width: 60,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 6,
                            marginRight: 12,
                          }}
                        />
                        <div className="flex-grow-1">
                          <div className="fw-bold" style={{ fontSize: 15 }}>
                            {item['course.title']}
                          </div>
                          <div className="text-muted" style={{ fontSize: 13 }}>
                            {item['course.instructorData']?.name}
                          </div>
                          <div className="text-success fw-bold" style={{ fontSize: 14 }}>
                            COP ${item.price?.toLocaleString('es-CO')}
                          </div>
                        </div>
                      </>
                    )}
                    <button
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={() => removeFromCart(item.courseId)}
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="modal-footer flex-column">
            {/* Sección de cupón de descuento */}
            <div className="w-100 mb-3">
              <label className="form-label fw-bold mb-2">Cupón de descuento</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder={CONSTANTS.LABEL_MESSAGE_DISCOUNT || 'Ingresa tu código de descuento'}
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                />
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  onClick={activateCoupon}
                  disabled={!couponCode.trim()}
                >
                  Aplicar
                </button>
              </div>
              {couponActivate && (
                <div className="text-danger mt-1 small">El cupón {couponCode} NO es válido</div>
              )}
              {discount && !couponActivate && !couponApplies && (
                <div className="text-danger mt-1 small">
                  El cupón {couponCode} no aplica para los productos en tu carrito.
                </div>
              )}
              {discount && !couponActivate && couponApplies && (
                <div className="text-success mt-1 small">
                  Se ha aplicado el cupón {couponCode}
                  {discount.price > 0 && (
                    <span>
                      {' '}
                      - Descuento:{' '}
                      {discountType === 1
                        ? `${discount.price}%`
                        : `COP $${discount.price.toLocaleString('es-CO')}`}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Mostrar subtotal y descuento si aplica */}
            {couponApplies && discount.price > 0 && (
              <div className="w-100 mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span>Subtotal:</span>
                  <span>COP ${subtotal.toLocaleString('es-CO')}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="text-success">Descuento:</span>
                  <span className="text-success">
                    - COP ${(subtotal - total).toLocaleString('es-CO')}
                  </span>
                </div>
              </div>
            )}

            <div className="w-100 d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold">Total:</span>
              <span className="fw-bold text-success fs-5">
                COP ${total.toLocaleString('es-CO')}
              </span>
            </div>
            <button
              className="BestCourses__button-primary b-0 w-100 mb-2"
              id={`${courseActionButtonPrefix}${cart['id']}`}
              style={{ width: '100%' }}
              disabled={cart.length === 0}
              onClick={() => setShowPaymentModal(true)}
            >
              {cart.find((item) => item.type === 'plan')
                ? 'Comprar plan'
                : `Comprar curso${cart.length > 1 ? 's' : ''}`}
            </button>
            {/* Modal de método de pago */}
            {showPaymentModal && (
              <div
                className="modal fade show"
                tabIndex="-1"
                style={{
                  display: 'block',
                  background: 'rgba(0,0,0,0.4)',
                  position: 'fixed',
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0,
                  zIndex: 3000,
                }}
                aria-modal="true"
                role="dialog"
                onClick={() => setShowPaymentModal(false)}
              >
                <div
                  className="modal-dialog modal-dialog-centered"
                  style={{ maxWidth: 400, margin: 'auto' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Elige un método de pago</h5>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => setShowPaymentModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <div className="d-flex flex-column gap-3">
                        {total === 0 && (
                          <button
                            className="btn btn-success w-100 mt-2"
                            onClick={async () => {
                              handleWithoutPayment();
                              clearCart();
                              setOpen(false);
                              setShowPaymentModal(false);
                            }}
                          >
                            Continuar
                          </button>
                        )}
                        {total !== 0 ? (
                          <>
                            <div
                              className={`d-flex align-items-center p-2 rounded border ${selectedPaymentMethod === 'mercadopago' ? 'border-success shadow-sm' : 'border-light'} cursor-pointer`}
                              style={{ cursor: 'pointer', borderWidth: 2 }}
                              onClick={() => setSelectedPaymentMethod('mercadopago')}
                            >
                              <i
                                className="fa fa-credit-card me-2"
                                style={{ fontSize: 20, color: '#198754' }}
                              ></i>
                              <span className="flex-grow-1">Pagos nacionales</span>
                              <input
                                type="radio"
                                checked={selectedPaymentMethod === 'mercadopago'}
                                onChange={() => setSelectedPaymentMethod('mercadopago')}
                                style={{ accentColor: '#198754', marginLeft: 8 }}
                              />
                            </div>
                            <div
                              className={`d-flex align-items-center p-2 rounded border ${selectedPaymentMethod === 'placetopay' ? 'border-success shadow-sm' : 'border-light'} cursor-pointer`}
                              style={{ cursor: 'pointer', borderWidth: 2 }}
                              onClick={() => setSelectedPaymentMethod('placetopay')}
                            >
                              <i
                                className="fa fa-flag-usa me-2"
                                style={{ fontSize: 20, color: '#198754' }}
                              ></i>
                              <span className="flex-grow-1">Pagos internacionales</span>
                              <input
                                type="radio"
                                checked={selectedPaymentMethod === 'placetopay'}
                                onChange={() => setSelectedPaymentMethod('placetopay')}
                                style={{ accentColor: '#198754', marginLeft: 8 }}
                              />
                            </div>
                          </>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                    {/* Mostrar footer solo si no es plan con total 0 */}
                    {!(
                      (cart[0]?.type === 'plan' && total === 0 && cart[0]?.price !== 0) ||
                      (cart[0]?.type !== 'plan' && total === 0 && cart[0]?.price !== 0)
                    ) && (
                      <div className="modal-footer">
                        <button
                          className="btn btn-success w-100"
                          disabled={!selectedPaymentMethod}
                          onClick={handleContinuePayment}
                          style={{ fontWeight: 'bold', fontSize: 18 }}
                        >
                          {cart.length > 0
                            ? `Pagar $${total.toLocaleString('es-CO')}`
                            : 'Continuar'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <button
              className="text_primary LandingHeader__secondary-button w-100"
              onClick={handleClearCart}
              disabled={cart.length === 0}
            >
              Vaciar carrito
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
